import { useState } from "react";
import { useNavigate } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { getSearchQuery } from "components/SearchInput/helpers";
import TablePagination from "components/Table/Pagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";
import usePagination from "components/Table/Pagination/usePagination";
import Button from "ds/components/Button";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Tooltip from "ds/components/Tooltip";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { BlueprintState, Template } from "types/generated";
import TemplateDeploymentEmpty from "views/Account/Template/Deployments/Empty";
import TemplateDeploymentsFiltersLayout from "views/Account/Template/Deployments/FiltersLayout";
import useDeployments from "views/Account/Template/Deployments/hooks/useDeployments";
import DeploymentsTable from "views/Account/Template/Deployments/Table";
import TemplateFeatureGateTooltip from "views/Account/TemplatesWorkbench/FeatureGate/Tooltip";
import { TemplateActions } from "views/Account/TemplatesWorkbench/FeatureGate/types";
import { AccountContext } from "views/AccountWrapper";

import {
  DEPLOYMENTS_LIMIT,
  DEPLOYMENTS_LIMIT_STORAGE_KEY,
  DEPRECATED_VERSION_DEPLOY_TOOLTIP,
} from "./constants";

type TemplateVersionDeploymentsProps = {
  templateId: string;
  versionId: string;
  template: Template;
  versionState: BlueprintState;
  versionDeprecatedAt: number | null | undefined;
};

const TemplateVersionDeployments = ({
  templateId,
  versionId,
  template,
  versionState,
  versionDeprecatedAt,
}: TemplateVersionDeploymentsProps) => {
  const { accountName } = useTypedContext(AccountContext);
  const navigate = useNavigate();
  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);
  const [itemsCount, setItemsCount] = useState<number>(0);

  useTitle(`Template Version Deployments · ${accountName}`);

  const {
    initialLoading,
    error,
    deployments,
    loadDeployments,
    endCursor,
    startCursor,
    noResults,
    isEmpty,
    loadingContent,
    predicates,
    paginationResetKey,
  } = useDeployments({ id: templateId, versionId });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadDeployments,
    endCursor,
    startCursor,
    visibleItemsCount: deployments.length,
    initialLimit: DEPLOYMENTS_LIMIT,
    allItemsCount: itemsCount,
    storageKey: DEPLOYMENTS_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const ErrorContent = useErrorHandle(error);

  const isPublished = versionState === BlueprintState.Published;

  const handleOpenDeployDrawer = () => {
    navigate(`/template/${templateId}/version/${versionId}/deploy`);
  };

  const isVersionDeprecated = !!versionDeprecatedAt;

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return (
      <>
        <PageInfoHeader title="Deployments" />
        <PageLayoutSkeleton />
      </>
    );
  }

  if (noResults) {
    return <NotFoundPage />;
  }

  const hasNoResultsWithFiltering = searchInput.length > 0 || predicates.length > 0;

  return (
    <>
      <PageInfoHeader title="Deployments">
        {isPublished && isVersionDeprecated && (
          <Tooltip
            active={isVersionDeprecated}
            placement="bottom"
            widthMode="maxWidthSm"
            on={(props) => (
              <Button {...props} variant="primary" size="medium" disabled>
                Deploy
              </Button>
            )}
          >
            {DEPRECATED_VERSION_DEPLOY_TOOLTIP}
          </Tooltip>
        )}
        {isPublished && !isVersionDeprecated && (
          <TemplateFeatureGateTooltip
            action={TemplateActions.EditTemplate}
            template={template}
            on={({ isDisabled, ...props }) => (
              <Button
                {...props}
                variant="primary"
                size="medium"
                onPress={handleOpenDeployDrawer}
                disabled={isDisabled}
              >
                Deploy
              </Button>
            )}
          />
        )}
      </PageInfoHeader>

      <TemplateDeploymentsFiltersLayout
        predicates={predicates}
        itemsCount={itemsCount}
        setItemsCount={setItemsCount}
        id={templateId}
      >
        {isEmpty && <TemplateDeploymentEmpty hasNoResults={hasNoResultsWithFiltering} />}

        {deployments.length > 0 && (
          <>
            <DeploymentsTable
              deployments={deployments}
              loading={loadingContent}
              rowsPerPage={rowsPerPage}
            />

            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={itemsCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </TemplateDeploymentsFiltersLayout>
    </>
  );
};

export default TemplateVersionDeployments;
